/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Col, Empty, Popover, Row, Skeleton, Space, Tooltip } from 'antd';
import { CloseOutlined, UploadOutlined } from '@ant-design/icons';
import { IoSendSharp } from 'react-icons/io5';
import { FaQuoteLeft, FaRegSmile } from 'react-icons/fa';
import { MdOutlineTextFormat } from 'react-icons/md';
import {
  mentionCommentParser,
  getMentionUuidFromNode,
  deltaToHtml,
} from 'src/utils/stringHelpers';
import {
  UploadFileApi,
  UserResponse,
  UploadResponse,
  UploadS3Response,
} from 'src/types';
import useComments from './useComment';
import CommentMessage from '../CommentMessage';
import FileUploadModal from '../FileUpload';
import RichTextEditor from '../RichTextEditor';
import { EmojiPicker } from '../EmojiPicker';
import apiRoutes from 'src/utils/apiRoutes';
import apiRequests from 'src/utils/api';

interface CommentProps {
  typeUuid: string;
  type: 'tasks' | 'projects' | 'requests';
  projectUuid: string;
  scrollClassName?: string;
  containerInputClass?: string;
  mentionTypes?: ('regular_user' | 'freelancer' | 'contact' | 'group')[];
  showCommentType?: boolean;
  showSendMessage?: boolean;
  onScrollIntoView?: any;
  onInitialLoaded?: () => void;
  onFinishSubmit?: (values: any, images: UploadFileApi[]) => void;
  onFilesDelete?: (uuid: string[]) => void;
}

export function useIsSmallScreen() {
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth <= 640);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  return isSmallScreen;
}

const Comment: FC<CommentProps> = ({
  typeUuid,
  type,
  projectUuid,
  scrollClassName,
  mentionTypes,
  showCommentType,
  showSendMessage = true,
  onScrollIntoView,
  onInitialLoaded,
  onFinishSubmit,
  onFilesDelete,
  containerInputClass = '',
}) => {
  const {
    inputRef,
    state,
    scrollRef,
    targetCommentId,
    setComment,
    handleUpdateComment,
    handleDeleteComment,
    handleAddComments,
    loadMore,
    handleDeleteUpload,
    handleUpdateReply,
    handleDeleteReply,
    handleAddReply,
    setReplyTo,
    setQuotedComment,
    handleAddReaction,
    handleRemoveReaction,
    handleRemoveReplyReaction,
    handleAddReplyReaction,
  } = useComments({
    typeUuid,
    type,
    onScrollIntoView,
    onInitialLoaded,
    onFilesDelete,
  });

  const { data, loading, adding, replyTo, replyToUuid, quotedComment, quotedCommentId, hasMore } = state;

  const handleReplyTo = (replyToUuid: string | null, replyTo: any, creator?: any) => {
    setQuotedComment(null, null);
    setReplyTo(replyToUuid, replyTo, creator);
  };

  const handleQuoteTo = (uuid: string, comment: any) => {
    const creator = comment.creator?.uuid !== user?.uuid ? comment.creator : undefined;
    setReplyTo(null, null);
    setQuotedComment(uuid, comment, creator);
  };

  const mentionableUrl =
    type === 'requests'
      ? `${apiRoutes.BASE_URL}/projects/requests/${typeUuid}/mentionables`
      : undefined;

  const [prefetchedMentionables, setPrefetchedMentionables] = useState<any[]>([]);

  useEffect(() => {
    const url =
      mentionableUrl ??
      (projectUuid
        ? `${apiRoutes.BASE_URL}/projects/${projectUuid}/mentionables`
        : null);

    if (!url) return;

    apiRequests.get(url).then((res) => {
      setPrefetchedMentionables(res.data.data ?? []);
    }).catch(() => {
      // silent — falls back to debounced fetch on @
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [images, setImages] = useState<UploadFileApi[]>([]);
  const [visible, setVisible] = useState(false);
  const [loadingImages, setLoadingImages] = useState(0);
  const [showEditorToolbar, setShowEditorToolbar] = useState(false);
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const [isEditorMultiline, setIsEditorMultiline] = useState(false);
  const singleLineHeightRef = useRef(38);

  useEffect(() => {
    const timer = setTimeout(() => {
      const editorEl = inputRef.current?.getQuill()?.getEditor()?.root;
      if (!editorEl) return;
      const cs = window.getComputedStyle(editorEl);
      const lineHeight = parseFloat(cs.lineHeight) || 23;
      const paddingTop = parseFloat(cs.paddingTop) || 5;
      const paddingBottom = parseFloat(cs.paddingBottom) || 5;
      singleLineHeightRef.current = lineHeight + paddingTop + paddingBottom + 4;
    }, 0);
    return () => clearTimeout(timer);
  }, [showEditorToolbar]);

  const user: UserResponse = useSelector(
    (globalState: any) => globalState.auth.user
  );

  if (loading && data?.length === 0) {
    return <Skeleton className="py-4" />;
  }

  const handleAddImages = (uploads: UploadFileApi[]) => {
    setImages([...images, ...uploads]);
    setVisible(false);
  };

  const handleDeleteImage = (uuid: string) => {
    setImages([...images?.filter((el) => el.response?.uuid !== uuid)]);
  };

  const handleScrollToComment = (uuid: string) => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    const targetEl = scrollEl.querySelector<HTMLElement>(`.comment-${uuid}`);
    if (!targetEl) return;
    scrollEl.scrollTop = targetEl.offsetTop;
    targetEl.classList.add('comment-highlight');
    setTimeout(() => targetEl.classList.remove('comment-highlight'), 2000);
  };

  const handleEmojiSelect = (emoji: { id: string; native: string }) => {
    const quill = inputRef.current?.getQuill()?.getEditor();
    if (quill) {
      const { index } = quill.getSelection(true);
      quill.insertEmbed(index, 'emoji-inline', { id: emoji.id, native: emoji.native });
      quill.setSelection(index + 1, 0);
    }
  };

  const emojiPickerContent = (
    <div ref={(ref) => {
      if (ref) ref.onclick = (e) => e.stopPropagation();
    }}>
      <EmojiPicker onSelect={handleEmojiSelect} />
    </div>
  );

  const handleSubmit = () => {
    if (adding) return;

    const comment = inputRef.current?.getQuill()?.getEditorContents() as string;
    const commentDelta = JSON.stringify(
      inputRef.current?.getQuill()?.getEditor().getContents()
    );

    if (!comment || inputRef.current?.isEmpty()) return;

    const values = {
      text: comment,
      text_delta: commentDelta,
      related_users: getMentionUuidFromNode(comment),
      type: 'comment',
      uploads: images.map((el) => el.response?.uuid),
      ...(quotedCommentId ? { quoted_id: quotedCommentId } : {}),
    };

    const statedImages: UploadFileApi[] = images.map((file) => {
      return {
        ...file,
        response: {
          ...file.response,
          stated: true,
        } as UploadS3Response,
      };
    });

    if (replyTo && replyToUuid) {
      handleAddReply(replyToUuid, values, () => {
        setComment('');
        setImages([]);
        setReplyTo(null, null);
        if (quotedCommentId) setQuotedComment(null, null);
        onFinishSubmit?.(values, statedImages);
      });
    } else {
      handleAddComments(values, () => {
        setComment('');
        setImages([]);
        if (quotedCommentId) setQuotedComment(null, null);
        onFinishSubmit?.(values, statedImages);
      });
    }
  };

  const parsedReplyText = replyTo?.text_delta
    ? deltaToHtml(replyTo.text_delta)
    : replyTo?.text;

  const parsedQuotedText = quotedComment?.text_delta
    ? deltaToHtml(quotedComment.text_delta)
    : quotedComment?.text;

  return (
    <>
      <FileUploadModal
        onOk={handleAddImages}
        visible={visible}
        closeUploadModal={() => setVisible(false)}
      />

      {!loading && data?.length === 0 ? (
        <Empty
          className="py-6"
          description={<span className="text-gray-400">No comments</span>}
        />
      ) : (
        <div
          ref={scrollRef}
          className={`w-full overflow-y-auto ${scrollClassName}`}
        >
          {hasMore && (
            <div className="text-center">
              <Button loading={loading} onClick={loadMore} type="primary">
                {loading ? 'Loading....' : 'Load more'}
              </Button>
            </div>
          )}

          <div className="flex flex-col">
            {data?.map((comment) => {
              return (
                <CommentMessage
                  key={comment.uuid}
                  comment={comment}
                  user={user}
                  targetCommentId={targetCommentId}
                  projectUuid={projectUuid}
                  showActions={showSendMessage}
                  showCommentType={showCommentType}
                  mentionTypes={mentionTypes}
                  mentionableUrl={mentionableUrl}
                  onReplyTo={handleReplyTo}
                  onQuoteTo={handleQuoteTo}
                  prefetchedMentionables={prefetchedMentionables}
                  onScrollToComment={handleScrollToComment}
                  onImgDelete={handleDeleteUpload}
                  onAddReaction={handleAddReaction}
                  onRemoveReaction={handleRemoveReaction}
                  onDelete={async (uuid) => {
                    await handleDeleteComment(uuid);
                    onFilesDelete?.(
                      comment.uploads.map((item: UploadResponse) => item.uuid)
                    );
                  }}
                  onUpdate={handleUpdateComment}
                  commentReplies={comment.replies.map((reply: any) => {
                    return (
                      <CommentMessage
                        key={reply.uuid}
                        parentEl={comment}
                        comment={reply}
                        showActions={showSendMessage}
                        targetCommentId={targetCommentId}
                        projectUuid={projectUuid}
                        mentionTypes={mentionTypes}
                        mentionableUrl={mentionableUrl}
                        prefetchedMentionables={prefetchedMentionables}
                        user={user}
                        onReplyTo={handleReplyTo}
                        onQuoteTo={handleQuoteTo}
                        onScrollToComment={handleScrollToComment}
                        onImgDelete={handleDeleteUpload}
                        onAddReaction={handleAddReplyReaction}
                        onRemoveReaction={handleRemoveReplyReaction}
                        onDelete={async (uuid, parentUuid) => {
                          await handleDeleteReply(uuid, parentUuid);
                          onFilesDelete?.(
                            comment.uploads.map(
                              (item: UploadResponse) => item.uuid
                            )
                          );
                        }}
                        onUpdate={handleUpdateReply}
                      />
                    );
                  })}
                />
              );
            })}
          </div>
        </div>
      )}

      <div className={`w-full pt-3 ${containerInputClass}`}>
        {showSendMessage && (
          <Space size={4} style={{ width: '100%' }} direction="vertical">
            <div style={{ display: 'flex', width: '100%', gap: 8, alignItems: 'flex-end' }}>
              <div className="chatInputWrapper" style={{ flex: 1 }}>
                {replyTo && parsedReplyText && (
                  <div className="replyTo">
                    <div className="line" />
                    <Row justify="space-between" gutter={16} wrap={false} align="middle">
                      <Col flex={1}>
                      <strong className='!mb-0'>{replyTo.creator.name}</strong>
                        <RichTextEditor
                          className="comment-styles resume"
                          defaultValue={mentionCommentParser(parsedReplyText)}
                          readonly
                          noContainer
                        />
                      </Col>
                      <Col>
                        <CloseOutlined onClick={() => setReplyTo(null, null)} />
                      </Col>
                    </Row>
                  </div>
                )}

                {quotedComment && parsedQuotedText && (
                  <div className="replyTo">
                    <div className="line" />
                    <Row justify="space-between" gutter={16} wrap={false} align="middle">
                      <Col flex={1}>
                        <div className='flex items-center'>
                          <FaQuoteLeft size={10} style={{ marginRight: 5, opacity: 0.5 }} />
                          <strong className='!mb-0'>{quotedComment.creator?.name}</strong>
                        </div>
                        <RichTextEditor
                          key={quotedComment.uuid}
                          className="comment-styles resume"
                          defaultValue={mentionCommentParser(parsedQuotedText)}
                          readonly
                          noContainer
                        />
                      </Col>
                      <Col>
                        <CloseOutlined onClick={() => setQuotedComment(null, null)} />
                      </Col>
                    </Row>
                  </div>
                )}

                <div className={`chatInputEditorRow${isEditorMultiline ? ' chatInputEditorRowMultiline' : ''}`}>
                  <RichTextEditor
                    ref={inputRef}
                    includeToolbar={true}
                    includeMention
                    projectId={projectUuid}
                    mentionableUrl={mentionableUrl}
                    placeholder="Add a comment [CMD/CTRL + Enter to send]"
                    className={`text-sm chat-input${!showEditorToolbar ? ' no-toolbar' : ''}`}
                    mentionTypes={mentionTypes}
                    onImageUploader={async (upload) => {
                      setImages((prev) => [...prev, upload]);
                    }}
                    onLoadingImage={setLoadingImages}
                    onShortEnterKey={handleSubmit}
                    onChange={(content) => {
                      const isEmpty = !content || content === '<p><br></p>';
                      setIsEditorEmpty(isEmpty);
                      const scrollHeight = inputRef.current?.getQuill()?.getEditor()?.root?.scrollHeight ?? 0;
                      setIsEditorMultiline((prev) => {
                        if (isEmpty) return false;
                        if (prev) return true;
                        return scrollHeight > singleLineHeightRef.current + 5;
                      });
                    }}
                  />

                  <div className="chatInputActions">
                    <Tooltip title="Formatting options">
                      <Button
                        type="text"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        icon={<MdOutlineTextFormat style={{ fontSize: '18px' }} />}
                        onClick={() => setShowEditorToolbar(!showEditorToolbar)}
                      />
                    </Tooltip>

                    <Tooltip title="Add emoji">
                      <Popover
                        content={emojiPickerContent}
                        trigger="click"
                        placement="bottomLeft"
                        getPopupContainer={() => document.body}
                        overlayClassName="no-padding-popover"
                      >
                        <Button
                          type="text"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          icon={<FaRegSmile style={{ fontSize: '18px' }} />}
                        />
                      </Popover>
                    </Tooltip>

                    <Tooltip title="Upload file">
                      <Button
                        type="text"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        icon={<UploadOutlined style={{ fontSize: '18px' }} />}
                        onClick={() => setVisible(true)}
                      />
                    </Tooltip>
                  </div>
                </div>

                {images.length > 0 && (
                  <Space wrap style={{ paddingTop: 4 }}>
                    {images.map((el) => (
                      <div
                        key={el.response?.uuid}
                        style={{ padding: '4px 8px', background: '#fff', borderRadius: 12 }}
                      >
                        <Space align="center">
                          {el.name}
                          <CloseOutlined onClick={() => handleDeleteImage(el.response?.uuid ?? '')} />
                        </Space>
                      </div>
                    ))}
                  </Space>
                )}
              </div>

              <Button
                style={{
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 6,
                  width: 36,
                  height: 36,
                }}
                loading={adding}
                disabled={loadingImages > 0 || isEditorEmpty}
                onClick={handleSubmit}
                type="primary"
                icon={<IoSendSharp />}
              />
            </div>
          </Space>
        )}
      </div>
    </>
  );
};

export default Comment;
